/**
 * TimelineView - displays tasks in a Gantt-like timeline
 */

import { useMemo, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameDay,
  differenceInDays,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isSameMonth,
} from 'date-fns';
import { useBoardStore } from '@stores/boardStore';
import type { Task } from '@services/api';

interface TimelineViewProps {
  onTaskClick: (taskId: string) => void;
  searchQuery?: string;
  filterPriority?: string;
  filterColumnId?: string;
}

const priorityColors: Record<string, string> = {
  urgent: '#EF4444',
  high: '#F97316',
  medium: '#EAB308',
  low: '#22C55E',
  none: '#9CA3AF',
};

export default function TimelineView({ onTaskClick, searchQuery = '', filterPriority = '', filterColumnId = '' }: TimelineViewProps) {
  const { tasks } = useBoardStore();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

  // Get tasks with dates, filtered by search and filters
  const tasksWithDates = useMemo(() => {
    const taskList: Task[] = [];
    Object.values(tasks).forEach((task) => {
      if (task && (task.startDate || task.dueDate)) {
        // Apply priority filter
        if (filterPriority && task.priority !== filterPriority) return;
        // Apply column filter
        if (filterColumnId && task.columnId !== filterColumnId) return;
        // Apply search filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const matches =
            task.title.toLowerCase().includes(query) ||
            (task.description?.toLowerCase().includes(query) ?? false) ||
            (task.taskKey?.toLowerCase().includes(query) ?? false) ||
            (task.labels?.some(label => label.name.toLowerCase().includes(query)) ?? false);
          if (!matches) return;
        }
        taskList.push(task);
      }
    });
    return taskList.sort((a, b) => {
      const dateA = a.startDate ?? a.dueDate ?? '';
      const dateB = b.startDate ?? b.dueDate ?? '';
      return dateA.localeCompare(dateB);
    });
  }, [tasks, searchQuery, filterPriority, filterColumnId]);

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      return {
        start: startOfWeek(currentMonth, { weekStartsOn: 1 }),
        end: endOfWeek(currentMonth, { weekStartsOn: 1 }),
      };
    }
    return {
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth),
    };
  }, [currentMonth, viewMode]);

  const days = useMemo(() => {
    return eachDayOfInterval(dateRange);
  }, [dateRange]);

  const today = new Date();

  const getTaskPosition = (task: Task) => {
    const startDate = task.startDate
      ? new Date(task.startDate)
      : task.dueDate
        ? new Date(task.dueDate)
        : null;
    const endDate = task.dueDate
      ? new Date(task.dueDate)
      : task.startDate
        ? new Date(task.startDate)
        : null;

    if (!startDate || !endDate) return null;

    const rangeStart = dateRange.start;
    const rangeEnd = dateRange.end;
    const totalDays = differenceInDays(rangeEnd, rangeStart) + 1;

    // Check if task is within current range
    if (endDate < rangeStart || startDate > rangeEnd) return null;

    const effectiveStart = startDate < rangeStart ? rangeStart : startDate;
    const effectiveEnd = endDate > rangeEnd ? rangeEnd : endDate;

    const startOffset = differenceInDays(effectiveStart, rangeStart);
    const duration = differenceInDays(effectiveEnd, effectiveStart) + 1;

    return {
      left: (startOffset / totalDays) * 100,
      width: (duration / totalDays) * 100,
    };
  };

  const handlePrevious = () => {
    setCurrentMonth((prev) =>
      viewMode === 'week' ? subMonths(prev, 0.25) : subMonths(prev, 1)
    );
  };

  const handleNext = () => {
    setCurrentMonth((prev) =>
      viewMode === 'week' ? addMonths(prev, 0.25) : addMonths(prev, 1)
    );
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-800">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevious}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md text-neutral-600 dark:text-neutral-400"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md"
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md text-neutral-600 dark:text-neutral-400"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('week')}
            className={`px-3 py-1 text-sm rounded-md ${
              viewMode === 'week'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`px-3 py-1 text-sm rounded-md ${
              viewMode === 'month'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
            }`}
          >
            Month
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-[800px]">
          {/* Days header */}
          <div className="flex border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 sticky top-0 z-10">
            <div className="w-64 flex-shrink-0 px-4 py-2 border-r border-neutral-200 dark:border-neutral-700 font-medium text-sm text-neutral-700 dark:text-neutral-300">
              Task
            </div>
            <div className="flex-1 flex">
              {days.map((day) => (
                <div
                  key={day.toISOString()}
                  className={`flex-1 text-center py-2 text-xs border-r border-neutral-200 dark:border-neutral-700 ${
                    isSameDay(day, today)
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                      : !isSameMonth(day, currentMonth)
                        ? 'text-neutral-400 dark:text-neutral-500'
                        : 'text-neutral-600 dark:text-neutral-400'
                  }`}
                >
                  <div>{format(day, 'EEE')}</div>
                  <div className="font-medium">{format(day, 'd')}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Task rows */}
          {tasksWithDates.length === 0 ? (
            <div className="p-12 text-center text-neutral-500 dark:text-neutral-400">
              No tasks with dates in this range
            </div>
          ) : (
            tasksWithDates.map((task) => {
              const position = getTaskPosition(task);

              return (
                <div
                  key={task.id}
                  className="flex border-b border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                >
                  <div
                    className="w-64 flex-shrink-0 px-4 py-3 border-r border-neutral-200 dark:border-neutral-700 cursor-pointer"
                    onClick={() => onTaskClick(task.id)}
                  >
                    <div className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {task.title}
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">{task.taskKey}</div>
                  </div>
                  <div className="flex-1 relative h-12">
                    {/* Day grid lines */}
                    <div className="absolute inset-0 flex">
                      {days.map((day) => (
                        <div
                          key={day.toISOString()}
                          className={`flex-1 border-r border-neutral-200 dark:border-neutral-700 ${
                            isSameDay(day, today) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        />
                      ))}
                    </div>
                    {/* Task bar */}
                    {position && (
                      <div
                        className="absolute top-2 h-8 rounded-md cursor-pointer hover:opacity-80 transition-opacity flex items-center px-2"
                        style={{
                          left: `${position.left}%`,
                          width: `${Math.max(position.width, 2)}%`,
                          backgroundColor:
                            priorityColors[task.priority] ?? priorityColors.none,
                        }}
                        onClick={() => onTaskClick(task.id)}
                      >
                        <span className="text-xs text-white font-medium truncate">
                          {task.title}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center gap-6 text-sm">
        <span className="text-neutral-500 dark:text-neutral-400">Priority:</span>
        {Object.entries(priorityColors).map(([priority, color]) => (
          <div key={priority} className="flex items-center gap-1">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: color }}
            />
            <span className="capitalize text-neutral-600 dark:text-neutral-400">{priority}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
